/**
 * Server-side reporting hooks for the Bookings and Quotes tables.
 *
 * Replaces the previous pattern on both pages, which fetched every row for the
 * account and filtered/paged in JavaScript. Because `max_rows = 1000` caps
 * PostgREST responses, that silently truncated any account past 1000 rows — and
 * the Quotes KPI cards were computed from the truncated array, so the
 * conversion rate was wrong at scale.
 *
 * Everything here pages with `.range()` and takes its totals from PostgREST's
 * exact `count`, never from `rows.length`.
 *
 * Shape follows `useLeads.ts`, including account resolution via impersonation.
 */

import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import {
  defaultColumnsFor,
  parseBookingFilters,
  parseQuoteFilters,
  parseSort,
  type BookingFilters,
  type BookingSortField,
  type QuoteFilters,
  type QuoteSortField,
  type QuoteStatusValue,
  type ReportResource,
  type SavedView,
  type TableSort,
} from '@limo/shared'
import { buildBookingsQuery } from '@/lib/reporting/buildBookingsQuery'
import { buildQuotesQuery } from '@/lib/reporting/buildQuotesQuery'
import {
  fetchAllForExport,
  type ExportProgress,
  type ExportResult,
} from '@/lib/reporting/csv'

/**
 * The active account, honouring admin impersonation.
 * Mirrors `useActiveAccountId` in `useLeads.ts` — reading `useAuth().account`
 * directly would leak the admin's own account into an impersonated view.
 */
function useActiveAccount() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  return isImpersonating ? impersonatedAccount : account
}

export function useActiveAccountId(): string | null {
  return useActiveAccount()?.id ?? null
}

/** The account's IANA timezone, used to resolve date presets. */
export function useAccountTimezone(): string {
  return useActiveAccount()?.timezone ?? 'UTC'
}

export interface ReportPage<T> {
  readonly rows: readonly T[]
  readonly total: number
}

// =============================================================================
// Bookings
// =============================================================================

export type BookingReportRow = Record<string, unknown>

export function useBookingsReport(
  filters: BookingFilters,
  sort: TableSort<BookingSortField>,
  page: number,
  pageSize: number
) {
  const accountId = useActiveAccountId()

  return useQuery<ReportPage<BookingReportRow>>({
    // filters/sort are plain serialisable objects so they key correctly. Their
    // identity is kept stable by useUrlReportFilters' useMemo — rebuilding them
    // each render would refetch continuously.
    queryKey: ['bookings-report', accountId, filters, sort, page, pageSize],
    queryFn: async () => {
      if (!accountId) return { rows: [], total: 0 }

      const from = page * pageSize
      const { data, count, error } = await buildBookingsQuery(
        supabase,
        accountId,
        filters,
        sort,
        { count: true }
      ).range(from, from + pageSize - 1)

      if (error) throw error

      return {
        rows: (data ?? []) as unknown as BookingReportRow[],
        // Exact server count — NOT data.length, which is capped at pageSize.
        total: count ?? 0,
      }
    },
    enabled: !!accountId,
    // Keep the previous page visible while the next loads, so paging doesn't
    // flash an empty table.
    placeholderData: (previous) => previous,
  })
}

// =============================================================================
// Quotes
// =============================================================================

export type QuoteReportRow = Record<string, unknown>

export function useQuotesReport(
  filters: QuoteFilters,
  sort: TableSort<QuoteSortField>,
  page: number,
  pageSize: number
) {
  const accountId = useActiveAccountId()

  return useQuery<ReportPage<QuoteReportRow>>({
    queryKey: ['quotes-report', accountId, filters, sort, page, pageSize],
    queryFn: async () => {
      if (!accountId) return { rows: [], total: 0 }

      const from = page * pageSize
      const { data, count, error } = await buildQuotesQuery(
        supabase,
        accountId,
        filters,
        sort,
        { count: true }
      ).range(from, from + pageSize - 1)

      if (error) throw error

      return {
        rows: (data ?? []) as unknown as QuoteReportRow[],
        total: count ?? 0,
      }
    },
    enabled: !!accountId,
    placeholderData: (previous) => previous,
  })
}

export interface QuoteReportStats {
  readonly total: number
  readonly active: number
  readonly converted: number
  readonly expired: number
  readonly conversionRate: number
}

/** Quote statuses still moving through the funnel (not converted, not expired). */
const ACTIVE_QUOTE_STATUSES: readonly QuoteStatusValue[] = [
  'trip_details',
  'vehicle_selected',
  'contact_provided',
  'quote_requested',
  'priced',
]

/**
 * Filter-aware KPI counts for the Quotes page.
 *
 * Replaces deriving stats from a fetched array, which was wrong past 1000 rows.
 * Four `head: true` count queries cost a round trip each and no payload, and —
 * unlike the old approach — they honour the active filters, so the cards move
 * with the table instead of describing a different data set.
 */
export function useQuoteReportStats(filters: QuoteFilters) {
  const accountId = useActiveAccountId()

  return useQuery<QuoteReportStats>({
    queryKey: ['quotes-report-stats', accountId, filters],
    queryFn: async () => {
      if (!accountId) {
        return { total: 0, active: 0, converted: 0, expired: 0, conversionRate: 0 }
      }

      const countFor = async (extra: Partial<QuoteFilters>): Promise<number> => {
        const { count, error } = await buildQuotesQuery(
          supabase,
          accountId,
          { ...filters, ...extra },
          undefined,
          { headOnly: true }
        )
        if (error) throw error
        return count ?? 0
      }

      /**
       * Intersect with any status filter the operator already set. If they have
       * narrowed to "Converted", the Active card must read 0 rather than
       * silently widening the filter it was given.
       */
      const narrow = (wanted: readonly QuoteStatusValue[]): QuoteStatusValue[] => {
        const current = filters.status
        if (!current || current.length === 0) return [...wanted]
        return current.filter((s) => wanted.includes(s))
      }

      const activeStatuses = narrow(ACTIVE_QUOTE_STATUSES)
      const convertedStatuses = narrow(['converted'])
      const expiredStatuses = narrow(['expired'])

      const [total, active, converted, expired] = await Promise.all([
        countFor({}),
        activeStatuses.length === 0 ? Promise.resolve(0) : countFor({ status: activeStatuses }),
        convertedStatuses.length === 0
          ? Promise.resolve(0)
          : countFor({ status: convertedStatuses }),
        expiredStatuses.length === 0 ? Promise.resolve(0) : countFor({ status: expiredStatuses }),
      ])

      return {
        total,
        active,
        converted,
        expired,
        conversionRate: total > 0 ? (converted / total) * 100 : 0,
      }
    },
    enabled: !!accountId,
    placeholderData: (previous) => previous,
  })
}

// =============================================================================
// Filter option sources
// =============================================================================

export interface VehicleOption {
  readonly id: string
  readonly name: string
}

/**
 * The account's vehicles, for the vehicle filter dropdown.
 *
 * No shared vehicles hook existed — vehicles are re-queried inline in ~8 places.
 * This one is scoped to what a filter needs (id + name) and cached, since the
 * list changes rarely.
 */
export function useAccountVehicles() {
  const accountId = useActiveAccountId()

  return useQuery<readonly VehicleOption[]>({
    queryKey: ['account-vehicles-options', accountId],
    queryFn: async () => {
      if (!accountId) return []
      const { data, error } = await supabase
        .from('vehicles')
        .select('id, name')
        .eq('account_id', accountId)
        .order('name', { ascending: true })

      if (error) throw error
      return (data ?? []) as VehicleOption[]
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  })
}

export interface UtmOptions {
  readonly sources: readonly string[]
  readonly mediums: readonly string[]
  readonly campaigns: readonly string[]
}

/**
 * Distinct UTM values actually present on this account's quotes.
 *
 * Driving the attribution dropdowns from real data avoids offering campaigns the
 * account never ran. Bounded to the most recent 1000 quotes so it stays one cheap
 * request — this is a picker, not a report. (1000 is also the server cap, so
 * asking for more would not return more anyway.)
 */
export function useUtmOptions() {
  const accountId = useActiveAccountId()

  return useQuery<UtmOptions>({
    queryKey: ['utm-options', accountId],
    queryFn: async () => {
      if (!accountId) return { sources: [], mediums: [], campaigns: [] }

      const { data, error } = await supabase
        .from('booking_quotes')
        .select('utm_source, utm_medium, utm_campaign')
        .eq('account_id', accountId)
        .not('utm_source', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1000)

      if (error) throw error

      const distinct = (key: 'utm_source' | 'utm_medium' | 'utm_campaign'): string[] => {
        const set = new Set<string>()
        for (const row of data ?? []) {
          const value = (row as Record<string, unknown>)[key]
          if (typeof value === 'string' && value.trim().length > 0) set.add(value)
        }
        return [...set].sort()
      }

      return {
        sources: distinct('utm_source'),
        mediums: distinct('utm_medium'),
        campaigns: distinct('utm_campaign'),
      }
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  })
}

// =============================================================================
// Export
// =============================================================================

/**
 * Fetch every row matching the current filters, for CSV export.
 *
 * Deliberately NOT a `useQuery` — this is a user-triggered action with progress,
 * and caching a 50k-row payload would be a memory leak in disguise.
 *
 * Uses the SAME query builder as the table, which is what makes the exported row
 * count equal the on-screen count.
 */
export function useReportExport(resource: ReportResource) {
  const accountId = useActiveAccountId()
  const [progress, setProgress] = useState<ExportProgress | null>(null)

  const run = useCallback(
    async (
      filters: BookingFilters | QuoteFilters,
      sort: TableSort<string>
    ): Promise<ExportResult<Record<string, unknown>>> => {
      if (!accountId) return { rows: [], truncated: false, total: 0 }

      setProgress({ fetched: 0, total: null })
      try {
        return await fetchAllForExport<Record<string, unknown>>(async (from, to) => {
          const query =
            resource === 'bookings'
              ? buildBookingsQuery(
                  supabase,
                  accountId,
                  filters as BookingFilters,
                  sort as TableSort<BookingSortField>,
                  { count: true }
                )
              : buildQuotesQuery(
                  supabase,
                  accountId,
                  filters as QuoteFilters,
                  sort as TableSort<QuoteSortField>,
                  { count: true }
                )

          const { data, count, error } = await query.range(from, to)
          return {
            data: (data ?? []) as unknown as Record<string, unknown>[],
            count: count ?? null,
            error: error ? { message: error.message } : null,
          }
        }, setProgress)
      } finally {
        setProgress(null)
      }
    },
    [accountId, resource]
  )

  return { run, progress, isExporting: progress !== null }
}

// =============================================================================
// Saved views
// =============================================================================

interface SavedViewRow {
  id: string
  account_id: string
  resource: string
  name: string
  filters: unknown
  sort: unknown
  columns: string[] | null
  created_by: string | null
  created_at: string
  updated_at: string
}

const BOOKING_SORT_FIELDS: readonly BookingSortField[] = [
  'created_at',
  'pickup_datetime',
  'total_amount',
  'balance_due',
  'customer_name',
  'booking_status',
  'payment_status',
]

const QUOTE_SORT_FIELDS: readonly QuoteSortField[] = [
  'created_at',
  'pickup_datetime',
  'total_amount',
  'expires_at',
  'status',
]

/**
 * Normalise a stored view.
 *
 * `filters` is JSONB written by a possibly-older build, so it is re-parsed
 * through the same validators the URL uses: unknown keys dropped, invalid values
 * coerced. A stale view must degrade, never throw and blank the page.
 */
function toSavedView(row: SavedViewRow): SavedView {
  const resource: ReportResource = row.resource === 'quotes' ? 'quotes' : 'bookings'
  const raw = (row.filters ?? {}) as Record<string, unknown>

  const filters = resource === 'bookings' ? parseBookingFilters(raw) : parseQuoteFilters(raw)

  const fields = resource === 'bookings' ? BOOKING_SORT_FIELDS : QUOTE_SORT_FIELDS
  const fallback = { field: 'created_at' as const, direction: 'desc' as const }
  const sort = row.sort ? parseSort(row.sort, fields, fallback) : null

  // An empty stored column list means "never picked" — fall back to defaults so
  // an export is never headerless.
  const columns =
    row.columns && row.columns.length > 0 ? row.columns : [...defaultColumnsFor(resource)]

  return {
    id: row.id,
    accountId: row.account_id,
    resource,
    name: row.name,
    filters: filters as Readonly<Record<string, unknown>>,
    sort: sort as TableSort | null,
    columns,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/** Postgres unique-violation — surfaced as a friendly message, not a raw error. */
const UNIQUE_VIOLATION = '23505'

export function useSavedViews(resource: ReportResource) {
  const accountId = useActiveAccountId()
  const { profile } = useAuth()
  const queryClient = useQueryClient()

  const queryKey = ['saved-views', accountId, resource]

  const list = useQuery<readonly SavedView[]>({
    queryKey,
    queryFn: async () => {
      if (!accountId) return []
      const { data, error } = await supabase
        .from('saved_report_views')
        .select('*')
        .eq('account_id', accountId)
        .eq('resource', resource)
        .order('name', { ascending: true })

      if (error) throw error
      return ((data ?? []) as unknown as SavedViewRow[]).map(toSavedView)
    },
    enabled: !!accountId,
  })

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey })
  }

  const create = useMutation({
    mutationFn: async (input: {
      name: string
      filters: Readonly<Record<string, unknown>>
      sort: TableSort<string> | null
      columns: readonly string[]
    }) => {
      if (!accountId) throw new Error('No active account')

      const { error } = await supabase.from('saved_report_views').insert({
        account_id: accountId,
        created_by: profile?.id ?? null,
        resource,
        // Trimmed so "Unpaid " and "Unpaid" can't both exist.
        name: input.name.trim(),
        filters: input.filters as never,
        sort: (input.sort ?? null) as never,
        columns: [...input.columns],
      })

      if (error) {
        if (error.code === UNIQUE_VIOLATION) {
          throw new Error(`A view named "${input.name.trim()}" already exists.`)
        }
        throw error
      }
    },
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: async (input: {
      id: string
      name?: string
      filters?: Readonly<Record<string, unknown>>
      sort?: TableSort<string> | null
      columns?: readonly string[]
    }) => {
      const patch: Record<string, unknown> = { updated_at: new Date().toISOString() }
      if (input.name !== undefined) patch.name = input.name.trim()
      if (input.filters !== undefined) patch.filters = input.filters
      if (input.sort !== undefined) patch.sort = input.sort
      if (input.columns !== undefined) patch.columns = [...input.columns]

      const { error } = await supabase
        .from('saved_report_views')
        .update(patch as never)
        .eq('id', input.id)

      if (error) {
        if (error.code === UNIQUE_VIOLATION) {
          throw new Error('A view with that name already exists.')
        }
        throw error
      }
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('saved_report_views').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return { list, create, update, remove }
}
